import React, { memo } from 'react';
import {
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Menu,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { BsArrowDownUp, BsThreeDotsVertical } from 'react-icons/bs';
import Div from '@jumbo/shared/Div';
import PropTypes from 'prop-types';

// ---------------------------Sortable Header Cell--------------------------- //
export const SortableTableCell = ({
  label = "",
  className = "",
  alignItems = "left",
  sortKey,
  handleSort,
  sortConfig,
}) => {
  return (
    <TableCell
      onClick={() => handleSort(sortKey)}
      className={className}
      sx={{
        cursor: "pointer",
        whiteSpace: "nowrap",
        textAlign: alignItems,
        width: "auto",
      }}
    >
      <Div sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {sortConfig?.key === sortKey && (
          <IconButton size="small" sx={{ p: 0 }}>
            <BsArrowDownUp size={14} />
          </IconButton>
        )}
      </Div>
    </TableCell>
  );
};

// ---------------------------Gear Icon Menu--------------------------- //
const ColumnMenu = ({ anchorEl, handleClose, GrearBox }) => {
  return (
    <Menu
      id="menu-appbar"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      sx={{
        mt: 6,
        maxHeight: 300,
      }}
    >
      <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 0 }}>
        Choose Columns
      </Typography>
      <FormGroup sx={{ p: 2 }}>
        {GrearBox?.map((item, index) => (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                checked={item.state}
                onChange={(e) => item.setState(e.target.checked)}
              />
            }
            label={item.label}
          />
        ))}
      </FormGroup>
    </Menu>
  );
};

// ---------------------------Main Table Head--------------------------- //
const CustomTableHead = ({
  headers = [],
  handleClickOpen = () => {},
  anchorEl = null,
  handleClose = () => {},
  sortConfig = { key: null, direction: "ascending" },
  handleSort = () => {},
  GrearBox = [],
  ActionMenus = { cell: true, actionIcon: true },
}) => {
  return (
    <>
      <TableHead className="table-head">
        <TableRow>
          {/* Dynamic Headers */}
          {headers?.map(
            (header, index) =>
              Boolean(header?.boolean) && (
                <SortableTableCell
                  key={index}
                  alignItems={header?.align}
                  label={header?.label}
                  className={header?.class}
                  sortKey={header?.sortKey}
                  handleSort={handleSort}
                  sortConfig={sortConfig}
                />
              )
          )}

          {/* Action Column */}
          {Boolean(ActionMenus?.cell) && (
            <TableCell
              sx={{
                textAlign: "center",
                width: "auto",
              }}
            >
              <Div
                sx={{
                  display: "flex",
                  justifyContent: ActionMenus?.actionIcon
                    ? "flex-end"
                    : "center",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Action
                </Typography>
                {Boolean(ActionMenus?.actionIcon) && (
                  <IconButton size="small" onClick={handleClickOpen}>
                    <BsThreeDotsVertical size={16} color="white" />
                  </IconButton>
                )}
              </Div>
            </TableCell>
          )}
        </TableRow>
      </TableHead>

      {/* Gear Icon Dropdown Menu */}
      {Boolean(ActionMenus?.actionIcon) && (
        <ColumnMenu
          anchorEl={anchorEl}
          handleClose={handleClose}
          GrearBox={GrearBox}
        />
      )}

      {/* Responsive Gear Icon (for mobile view) */}
      {Boolean(ActionMenus?.actionIcon) && (
        <Div
          sx={{
            mr: 1,
            display: { xs: "flex", md: "none" },
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <IconButton onClick={handleClickOpen}>
            <BsThreeDotsVertical size={18} color="white" />
          </IconButton>
        </Div>
      )}
    </>
  );
};

// ---------------------------PropTypes--------------------------- //
SortableTableCell.propTypes = {
  label: PropTypes.string,
  className: PropTypes.string,
  alignItems: PropTypes.string,
  sortKey: PropTypes.string,
  handleSort: PropTypes.func,
  sortConfig: PropTypes.object,
};

ColumnMenu.propTypes = {
  anchorEl: PropTypes.any,
  handleClose: PropTypes.func,
  GrearBox: PropTypes.array,
};

CustomTableHead.propTypes = {
  headers: PropTypes.array,
  handleClickOpen: PropTypes.func,
  anchorEl: PropTypes.any,
  handleClose: PropTypes.func,
  sortConfig: PropTypes.object,
  handleSort: PropTypes.func,
  GrearBox: PropTypes.array,
  ActionMenus: PropTypes.object,
};

export default memo(CustomTableHead);
